// src/components/AIBadge.tsx
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export default function AIBadge(): JSX.Element {
  const [reducedMotion, setReducedMotion] = useState<boolean>(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    const handler = (e: MediaQueryListEvent): void => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const hoverAnim = reducedMotion ? undefined : { scale: 1.01 };

  // Gradient for the bulb — use theme colors (via CSS variables in index.css)
  const bulbGradient = 'linear-gradient(90deg, var(--color-primary) 0%, var(--color-secondary) 100%)';
  const bulbGlow = '0 0 18px rgba(168,85,247,0.16)';

  return (
    <motion.div
      className="inline-flex items-center gap-2 sm:gap-4 px-4 py-1.5 sm:px-6 sm:py-2.5 rounded-full border-2 border-primary/25 bg-white/80 dark:bg-gradient-to-r dark:from-[#201433] dark:via-[#3b2a55] dark:to-[#16223a] shadow-sm dark:shadow-[0_8px_30px_rgba(16,11,20,0.6)] backdrop-blur-md max-w-full whitespace-nowrap"
      whileHover={hoverAnim}
      whileFocus={hoverAnim}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      tabIndex={0}
      role="status"
      aria-label="AI-Inspired Document Processing"
    >
      {/* left bulb - gradient + subtle shimmer (respects reduced motion) */}
      <motion.span
        aria-hidden
        className="flex-shrink-0 rounded-full"
        style={{ width: 10, height: 10, background: bulbGradient, backgroundSize: '200% 100%', boxShadow: bulbGlow }}
        animate={reducedMotion ? undefined : { backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
        transition={reducedMotion ? undefined : { duration: 4.2, repeat: Infinity, ease: 'linear' }}
      />

      {/* headline text - responsive sizing and single-line guarantee */}
      <span className="text-xs sm:text-sm md:text-base font-normal text-text-main/95 tracking-tight whitespace-nowrap">
        AI-Inspired Document Processing
      </span>
    </motion.div>
  );
}